import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { api, formatApiError } from "../../lib/api";
import { Upload, Check, AlertTriangle, X, ChevronLeft, History } from "lucide-react";
import { toast } from "sonner";

// =============================================================================
// Two-step bulk upload:
//   1) Upload file -> POST /preview -> show per-row plan + approvals
//   2) Confirm -> POST /commit -> show final result
// =============================================================================

const STATUS_BG = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  error: "bg-red-50 text-red-700 border-red-200",
};

export default function AdminBulkUpload() {
  const [step, setStep] = useState("upload"); // upload | review | result
  const [tab, setTab] = useState("import"); // import | history
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [excludedRows, setExcludedRows] = useState(new Set());
  const [approveVehicles, setApproveVehicles] = useState(false);
  const [approveSuppliers, setApproveSuppliers] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    const header = "SKU,Product Name,Product Type,Brand,Price,Quantity,Side,Heated,Blind spot,Auto dimming,Camera,Vehicle Year,Vehicle Make,Vehicle Model,Vehicle Trim,Supplier Name,Supplier SKU,Image URLs,SEO Title,SEO Description,Description";
    const example = 'LAG-FER-1001,Ferrari 488 GTB Left Side Mirror,side-mirror,Ferrari,689.00,6,left,yes,yes,no,no,2018,Ferrari,488 GTB,Spider,Premium Glass Co,PGC-FER-488-L,https://example.com/p/1.jpg;https://example.com/p/2.jpg,Ferrari 488 GTB Left Mirror | Lecis,OEM-style heated left mirror for 2018 Ferrari 488 GTB Spider.,Premium OEM-style left side mirror.';
    const csv = header + "\n" + example + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lecis-bulk-upload-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const submitPreview = async (e) => {
    e?.preventDefault?.();
    if (!file) { toast.error("Pick a file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/bulk-upload/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setExcludedRows(new Set());
      // Default approvals OFF — admin must opt in
      setApproveVehicles(false);
      setApproveSuppliers(false);
      setStep("review");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const toggleRow = (rowIndex) => {
    setExcludedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/admin/products/bulk-upload/commit", {
        preview_id: preview.preview_id,
        approve_new_vehicles: approveVehicles,
        approve_new_suppliers: approveSuppliers,
        approve_partial: true,
        excluded_rows: Array.from(excludedRows),
      });
      setResult(data);
      setStep("result");
      if (data.status === "success") toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
      else if (data.status === "partial") toast.warning(`Partial import — ${data.skipped} skipped`);
      else toast.error("Import failed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCommitting(false);
    }
  };

  const reset = () => {
    setStep("upload");
    setFile(null);
    setPreview(null);
    setResult(null);
    setExcludedRows(new Set());
  };

  // ---------- Step 1: upload ----------
  if (step === "upload") {
    return (
      <AdminLayout title="Bulk upload">
        <div className="flex gap-1 mb-6 border-b border-zinc-200" data-testid="bulk-tabs">
          <TabButton active={tab === "import"} onClick={() => setTab("import")} testid="tab-import">Import</TabButton>
          <TabButton active={tab === "history"} onClick={() => setTab("history")} testid="tab-history">
            <History size={12} className="inline mr-1" /> Import History
          </TabButton>
        </div>

        {tab === "history" ? <HistoryTab /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="bulk-upload-form">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Step 1 · Upload spreadsheet</div>
              <button onClick={downloadTemplate} data-testid="download-template-btn" className="inline-flex items-center gap-1.5 text-xs text-zinc-600 hover:text-zinc-900 border border-zinc-200 hover:border-zinc-400 px-3 py-1.5 rounded">
                <Download size={12} /> Download CSV template
              </button>
            </div>
            <form onSubmit={submitPreview}>
              <label className="block border-2 border-dashed border-zinc-300 hover:border-zinc-500 rounded-md p-8 text-center cursor-pointer">
                <Upload size={32} className="mx-auto text-zinc-400 mb-3" />
                <div className="text-sm font-medium mb-1">{file ? file.name : "Drop or click to upload"}</div>
                <div className="text-xs text-zinc-500">.csv, .xlsx, .xls</div>
                <input
                  data-testid="bulk-file-input"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
              <button
                data-testid="bulk-preview-submit"
                disabled={!file || uploading}
                className="mt-4 w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white font-medium text-sm rounded-sm disabled:opacity-50"
              >
                {uploading ? "Parsing…" : "Preview import"}
              </button>
            </form>
            <div className="mt-4 text-xs text-zinc-500">
              Nothing is saved until you confirm the preview on the next screen.
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-md p-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-4">Spreadsheet columns</div>
            <pre className="bg-zinc-50 border border-zinc-200 p-3 text-[11px] font-mono overflow-x-auto rounded leading-relaxed">
{`SKU* (required)             Vehicle Year
Product Name* (required)    Vehicle Make
Price* (required)           Vehicle Model
Product Type / Side         Vehicle Trim
  side mirror values:       Supplier Name
    left | right | pair     Supplier SKU
  windshield value:         Image URLs (semicolon-separated)
    windshield              SEO Title
Quantity                    SEO Description
Heated (yes/no)             Description
Blind spot (yes/no)
Auto dimming (yes/no)
Camera (yes/no)`}
            </pre>
            <ul className="text-xs text-zinc-600 mt-4 space-y-1.5">
              <li>• Column names are case-insensitive and accept common aliases.</li>
              <li>• Multi-fitment: repeat rows with the same SKU and different vehicle Y/M/M/T.</li>
              <li>• Missing brands / models / trims / suppliers can be created — admin approves on the next screen.</li>
            </ul>
          </div>
        </div>
        )}
      </AdminLayout>
    );
  }

  // ---------- Step 2: review ----------
  if (step === "review" && preview) {
    return <ReviewStep
      preview={preview}
      excludedRows={excludedRows}
      onToggleRow={toggleRow}
      approveVehicles={approveVehicles}
      setApproveVehicles={setApproveVehicles}
      approveSuppliers={approveSuppliers}
      setApproveSuppliers={setApproveSuppliers}
      committing={committing}
      onBack={reset}
      onCommit={commit}
    />;
  }

  // ---------- Step 3: result ----------
  if (step === "result" && result) {
    return <ResultStep result={result} onReset={reset} />;
  }

  return null;
}


function ReviewStep({ preview, excludedRows, onToggleRow, approveVehicles, setApproveVehicles, approveSuppliers, setApproveSuppliers, committing, onBack, onCommit }) {
  const sum = preview.summary;

  const willCommit = useMemo(() => {
    let creates = 0, updates = 0, skips = 0;
    for (const r of preview.rows) {
      const excluded = excludedRows.has(r.row_index);
      const needsVehApproval = r.requires_approval?.new_vehicles > 0;
      const needsSupApproval = r.requires_approval?.new_suppliers > 0;
      if (r.status === "error" || excluded) { skips++; continue; }
      if (needsVehApproval && !approveVehicles) { skips++; continue; }
      if (needsSupApproval && !approveSuppliers) { skips++; continue; }
      if (r.action === "create") creates++;
      else if (r.action === "update") updates++;
    }
    return { creates, updates, skips };
  }, [preview, excludedRows, approveVehicles, approveSuppliers]);

  return (
    <AdminLayout title="Bulk upload · Review">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-900 mb-6">
        <ChevronLeft size={12} /> Start over
      </button>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="preview-summary">
        <Tile label="Total rows" value={preview.total_rows} />
        <Tile label="Valid" value={preview.valid_count} tone="ok" />
        <Tile label="Warnings" value={preview.warning_count} tone="warning" />
        <Tile label="Errors" value={preview.error_count} tone="error" />
        <Tile label="Will commit" value={willCommit.creates + willCommit.updates} tone="primary" />
      </div>

      {/* Approval panel */}
      <div className="bg-white border border-zinc-200 rounded-md p-5 mb-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-4">Approvals</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ApprovalToggle
            checked={approveVehicles}
            onChange={setApproveVehicles}
            label="Create missing vehicle records"
            details={
              sum.new_vehicles_count === 0
                ? "No new vehicles needed."
                : `${sum.new_vehicles_count} vehicle record(s) will be created${sum.new_brands.length ? `, including new brand(s): ${sum.new_brands.join(", ")}` : ""}.`
            }
            disabled={sum.new_vehicles_count === 0}
            testId="approve-vehicles"
          />
          <ApprovalToggle
            checked={approveSuppliers}
            onChange={setApproveSuppliers}
            label="Create missing suppliers"
            details={
              sum.new_suppliers.length === 0
                ? "No new suppliers needed."
                : `New supplier(s): ${sum.new_suppliers.join(", ")}.`
            }
            disabled={sum.new_suppliers.length === 0}
            testId="approve-suppliers"
          />
        </div>
        {sum.seo_pages_count > 0 && (
          <div className="mt-3 text-xs text-zinc-600">
            <Check size={12} className="inline text-emerald-600 mr-1" />
            {sum.seo_pages_count} SEO page override(s) will be upserted to <code>seo_pages</code>.
          </div>
        )}
      </div>

      {/* Row table */}
      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-zinc-200 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
          Per-row plan
        </div>
        <table className="w-full text-sm" data-testid="preview-rows-table">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50">
            <tr>
              <th className="text-left px-5 py-2 w-12">Inc.</th>
              <th className="text-left px-5 py-2 w-14">Row</th>
              <th className="text-left px-5 py-2">SKU</th>
              <th className="text-left px-5 py-2">Name</th>
              <th className="text-left px-5 py-2">Action</th>
              <th className="text-left px-5 py-2">Status</th>
              <th className="text-left px-5 py-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((r) => {
              const excluded = excludedRows.has(r.row_index);
              const disabled = r.status === "error";
              const needsVeh = r.requires_approval?.new_vehicles > 0;
              const needsSup = r.requires_approval?.new_suppliers > 0;
              const blockedByApproval = (needsVeh && !approveVehicles) || (needsSup && !approveSuppliers);
              return (
                <tr key={r.row_index} className={`border-t border-zinc-100 ${disabled || excluded || blockedByApproval ? "opacity-60" : ""}`} data-testid={`preview-row-${r.row_index}`}>
                  <td className="px-5 py-2">
                    <input
                      type="checkbox"
                      data-testid={`preview-include-${r.row_index}`}
                      checked={!excluded && !disabled}
                      disabled={disabled}
                      onChange={() => onToggleRow(r.row_index)}
                      className="w-4 h-4"
                    />
                  </td>
                  <td className="px-5 py-2 font-mono text-xs">{r.row_index}</td>
                  <td className="px-5 py-2 font-mono text-xs">{r.sku || "—"}</td>
                  <td className="px-5 py-2 truncate max-w-[260px]">{r.name || "—"}</td>
                  <td className="px-5 py-2">
                    <ActionPill action={blockedByApproval ? "skip" : r.action} />
                  </td>
                  <td className="px-5 py-2">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-5 py-2 text-xs text-zinc-600">
                    {r.errors?.map((e, i) => <div key={`e${i}`} className="text-red-600">• {e}</div>)}
                    {r.warnings?.map((w, i) => <div key={`w${i}`} className="text-amber-700">• {w}</div>)}
                    {blockedByApproval && !disabled && (
                      <div className="text-zinc-500">• Blocked by approval flag above</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* CTA bar */}
      <div className="sticky bottom-0 bg-white border-t border-zinc-200 -mx-8 px-8 py-4 flex items-center justify-between">
        <div className="text-sm text-zinc-600">
          <span className="font-mono">{willCommit.creates}</span> create · <span className="font-mono">{willCommit.updates}</span> update · <span className="font-mono">{willCommit.skips}</span> skip
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-sm text-zinc-500 hover:text-zinc-900 px-3 h-10">Cancel</button>
          <button
            data-testid="bulk-commit-btn"
            disabled={committing || (willCommit.creates + willCommit.updates) === 0}
            onClick={onCommit}
            className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium tracking-wide px-6 h-10 rounded-sm disabled:opacity-40"
          >
            {committing ? "Importing…" : `Commit import (${willCommit.creates + willCommit.updates})`}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}


function ResultStep({ result, onReset }) {
  const ok = result.status === "success";
  return (
    <AdminLayout title="Bulk upload · Result">
      <div className="bg-white border border-zinc-200 rounded-md p-6 mb-6 max-w-3xl" data-testid="bulk-result">
        <div className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold mb-3 ${ok ? "text-emerald-700" : (result.status === "partial" ? "text-amber-700" : "text-red-700")}`}>
          {ok ? <Check size={14} /> : result.status === "partial" ? <AlertTriangle size={14} /> : <X size={14} />}
          {result.status}
        </div>
        <h2 className="font-display text-2xl mb-4">Import complete</h2>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          <Tile label="Created" value={result.created} tone="ok" />
          <Tile label="Updated" value={result.updated} />
          <Tile label="Skipped" value={result.skipped} tone={result.skipped > 0 ? "warning" : undefined} />
          <Tile label="New vehicles" value={result.new_vehicles_created} />
          <Tile label="New suppliers" value={result.new_suppliers_created} />
          <Tile label="SEO pages" value={result.seo_pages_upserted} />
        </div>

        {result.errors?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Errors</div>
            <div className="border border-zinc-200 rounded max-h-72 overflow-y-auto">
              {result.errors.map((e, i) => (
                <div key={i} className="text-xs font-mono border-b border-zinc-100 px-3 py-1.5">
                  Row {e.row}{e.sku ? ` · ${e.sku}` : ""}: <span className="text-red-600">{e.error}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center gap-3">
          <button onClick={onReset} data-testid="bulk-restart-btn" className="bg-zinc-900 hover:bg-zinc-800 text-white text-sm px-5 h-10 rounded-sm">Import another file</button>
          <Link to="/admin/products" className="text-sm text-zinc-500 hover:text-zinc-900">View products →</Link>
        </div>
      </div>
    </AdminLayout>
  );
}


// =============================================================================
// Atoms
// =============================================================================
function Tile({ label, value, tone }) {
  const tones = {
    ok: "border-emerald-200 bg-emerald-50",
    warning: "border-amber-200 bg-amber-50",
    error: "border-red-200 bg-red-50",
    primary: "border-zinc-900 bg-zinc-900 text-white",
  };
  return (
    <div className={`border rounded p-3 ${tones[tone] || "border-zinc-200 bg-white"}`}>
      <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${tone === "primary" ? "text-zinc-300" : "text-zinc-500"}`}>{label}</div>
      <div className="text-2xl font-mono mt-1">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-block text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 border rounded ${STATUS_BG[status] || "border-zinc-200"}`}>
      {status}
    </span>
  );
}

function ActionPill({ action }) {
  const map = {
    create: "border-emerald-200 bg-emerald-50 text-emerald-700",
    update: "border-blue-200 bg-blue-50 text-blue-700",
    skip:   "border-zinc-200 bg-zinc-50 text-zinc-600",
  };
  return (
    <span className={`inline-block text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 border rounded ${map[action] || "border-zinc-200"}`}>
      {action}
    </span>
  );
}

function ApprovalToggle({ checked, onChange, label, details, disabled, testId }) {
  return (
    <label className={`flex items-start gap-3 p-3 rounded border ${disabled ? "border-zinc-100 opacity-50" : "border-zinc-200 cursor-pointer hover:border-zinc-400"}`}>
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="w-4 h-4 mt-0.5"
      />
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-zinc-600 mt-1">{details}</div>
      </div>
    </label>
  );
}

function TabButton({ children, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2.5 text-sm tracking-wide -mb-px border-b-2 ${active ? "border-zinc-900 text-zinc-900 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
    >
      {children}
    </button>
  );
}

function HistoryTab() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/csv-import-jobs").then(({ data }) => { setJobs(data.jobs || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  const tone = (s) => ({ success: "text-emerald-700", partial: "text-amber-700", failed: "text-red-700", pending_review: "text-zinc-500", running: "text-blue-700" }[s] || "text-zinc-700");
  return (
    <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="bulk-history-table">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50">
          <tr>
            <th className="text-left px-5 py-3">Started</th>
            <th className="text-left px-5 py-3">Filename</th>
            <th className="text-left px-5 py-3">By</th>
            <th className="text-left px-5 py-3">Status</th>
            <th className="text-right px-5 py-3">Created</th>
            <th className="text-right px-5 py-3">Updated</th>
            <th className="text-right px-5 py-3">Errors</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={7} className="text-center text-zinc-500 py-10">Loading…</td></tr>}
          {!loading && jobs.length === 0 && <tr><td colSpan={7} className="text-center text-zinc-500 py-10">No import jobs yet.</td></tr>}
          {jobs.map((j) => (
            <tr key={j.id} className="border-t border-zinc-100" data-testid={`history-row-${j.id.slice(0,8)}`}>
              <td className="px-5 py-3 font-mono text-xs">{j.started_at ? new Date(j.started_at).toLocaleString() : "—"}</td>
              <td className="px-5 py-3 truncate max-w-[260px]">{j.filename || "—"}</td>
              <td className="px-5 py-3 text-zinc-500 text-xs">{j.triggered_by_email || "—"}</td>
              <td className={`px-5 py-3 text-[10px] uppercase tracking-[0.2em] font-bold ${tone(j.status)}`}>{j.status}</td>
              <td className="px-5 py-3 text-right font-mono">{j.created_count ?? 0}</td>
              <td className="px-5 py-3 text-right font-mono">{j.updated_count ?? 0}</td>
              <td className="px-5 py-3 text-right font-mono">{j.error_count ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
